import {
   IntegrationMessageChainFragment,
   IntegrationMessageFragment,
   MessageChainChildrenDocument,
   MessageChainChildrenQuery,
   MessageChainChildrenQueryVariables,
   MessageChainNodeDocument,
   MessageChainNodeQuery,
   MessageChainNodeQueryVariables,
   useMessageDetailPageGetQuery,
   useRerunMessageMutation
} from "../../Queries";
import {
   Alert,
   Box,
   Button,
   Chip,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   Divider,
   IconButton,
   LinearProgress,
   Stack,
   Tab,
   Tabs,
   Tooltip
} from "@mui/material";
import {Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator} from "@mui/lab";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import {BodyText, TitleText} from "../../layout/Typography";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin} from "../../atoms/CurrentUser";
import {useApolloClient} from "@apollo/client";
import dayjs from "dayjs";

import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-handlebars";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-tomorrow";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {LinkTo} from "../../components/LinkTo";
import {ConnectorEditPagePath} from "./ConnectorEditPage";

type MessageDetailPanelProps = {
   messageId: string
   onRerun?: () => void
}

const RERUN_WARNING = "Rerunning performs the connector action again. This is not exactly-once - " +
   "if the original action actually succeeded, the downstream side effect (order update, mail, label, ...) " +
   "may happen twice. Continue?";

/** Upper bound on how many messages of a rerun lineage we walk - a runaway chain must not stall the dialog. */
const CHAIN_MAX_NODES = 25;

const TIMESTAMP_FORMAT = 'HH:mm:ss.SSS, D. MMM YYYY';

const POLL_MS = 2000;

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export type MessageStatus = {
   label: string
   color: ChipColor
}

/**
 * Single source of truth for how a message state is presented - shared with the list so a row and
 * its dialog never disagree. messageFailed is only written once the connector has given a verdict.
 */
export const messageStatus = (msg: {
   sendAt?: Date | null
   sent?: Date | null
   failed?: Date | null
   messageFailed?: boolean | null
   messageIgnored?: boolean | null
}): MessageStatus => {
   if (msg.messageFailed === undefined || msg.messageFailed === null) {
      if (msg.failed) {
         return {label: 'Not delivered', color: 'error'}
      }
      if (msg.sendAt && !msg.sent) {
         return {label: 'Scheduled', color: 'info'}
      }
      return {label: 'Awaiting response', color: 'warning'}
   }
   if (msg.messageFailed) {
      return {label: 'Failed', color: 'error'}
   }
   if (msg.messageIgnored) {
      return {label: 'Ignored', color: 'secondary'}
   }
   return {label: 'Success', color: 'success'}
}

/** A result is terminal once the connector replied or we gave up delivering - only then may it be rerun. */
export const isTerminal = (msg: { failed?: Date | null, resultTs?: Date | null }) => !!msg.failed || !!msg.resultTs

const formatDuration = (ms: number): string => {
   if (ms < 1000) {
      return Math.round(ms) + 'ms'
   }
   if (ms < 60000) {
      return (ms / 1000).toFixed(ms < 10000 ? 2 : 1) + 's'
   }
   const minutes = Math.floor(ms / 60000);
   return minutes + 'm ' + Math.round((ms % 60000) / 1000) + 's'
}

const timeBetween = (from?: Date | null, to?: Date | null): string | undefined => {
   if (!from || !to) {
      return undefined
   }
   return formatDuration(new Date(to).getTime() - new Date(from).getTime())
}

/** Digs the human readable failure out of whatever shape the connector chose to answer with. */
export const extractError = (result: any, depth: number = 0): string | undefined => {
   if (!result || depth > 4) {
      return undefined
   }
   if (typeof result === 'string') {
      return result
   }
   if (Array.isArray(result)) {
      for (const entry of result) {
         const nested = extractError(entry, depth + 1);
         if (nested) {
            return nested
         }
      }
      return undefined
   }
   if (typeof result !== 'object') {
      return undefined
   }
   for (const key of ['error', 'errorMessage', 'message', 'reason', 'detail', 'details', 'statusText', 'errors']) {
      const value = result[key];
      if (typeof value === 'string' && value.trim()) {
         return value
      }
      if (value && typeof value === 'object') {
         const nested = extractError(value, depth + 1);
         if (nested) {
            return nested
         }
      }
   }
   return undefined
}

const copyToClipboard = (value: string) => {
   navigator.clipboard?.writeText(value)
}

type ChainState = {
   nodes: IntegrationMessageChainFragment[]
   loading: boolean
   truncated: boolean
}

/**
 * Resolves the full rerun lineage of a message: walk parentId up to the original attempt, then walk
 * back down through every rerun spawned from it. Returned oldest first.
 */
const useMessageChain = (message?: IntegrationMessageFragment): ChainState & { reload: () => void } => {
   const client = useApolloClient();
   const [state, setState] = useState<ChainState>({nodes: [], loading: false, truncated: false});
   const [reloads, setReloads] = useState<number>(0);

   const messageId: string | undefined = message?._id;
   const parentId: string | undefined = message?.parentId;
   const seed = message;

   useEffect(() => {
      if (!messageId || !seed) {
         setState({nodes: [], loading: false, truncated: false});
         return
      }
      let cancelled = false;
      const load = async () => {
         setState(current => ({...current, loading: true}));
         const nodes = new Map<string, IntegrationMessageChainFragment>();
         nodes.set(messageId, seed);
         let rootId: string = messageId;
         let cursor: string | undefined | null = parentId;
         let truncated = false;
         try {
            while (cursor && nodes.size < CHAIN_MAX_NODES) {
               const response = await client.query<MessageChainNodeQuery, MessageChainNodeQueryVariables>({
                  query: MessageChainNodeDocument,
                  variables: {messageId: cursor},
                  fetchPolicy: "no-cache"
               });
               const node: IntegrationMessageChainFragment | null | undefined = response.data?.IntegrationMessageById;
               if (!node || nodes.has(node._id)) {
                  break
               }
               nodes.set(node._id, node);
               rootId = node._id;
               cursor = node.parentId as string | null | undefined;
            }
            truncated = truncated || !!cursor;

            const queue: string[] = [rootId];
            while (queue.length > 0) {
               if (nodes.size >= CHAIN_MAX_NODES) {
                  truncated = true;
                  break
               }
               const next = queue.shift() as string;
               const response = await client.query<MessageChainChildrenQuery, MessageChainChildrenQueryVariables>({
                  query: MessageChainChildrenDocument,
                  variables: {parentId: next},
                  fetchPolicy: "no-cache"
               });
               const children: IntegrationMessageChainFragment[] = response.data?.IntegrationMessages?.items || [];
               children.forEach(child => {
                  if (!nodes.has(child._id)) {
                     nodes.set(child._id, child);
                  }
                  queue.push(child._id);
               });
            }
         } catch (error) {
            // A partially resolved lineage is still more useful than none - fall through with what we have.
         }
         if (cancelled) {
            return
         }
         const ordered = Array.from(nodes.values())
            .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
         setState({nodes: ordered, loading: false, truncated: truncated});
      }
      load();
      return () => {
         cancelled = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [client, messageId, parentId, reloads]);

   const reload = useCallback(() => setReloads(value => value + 1), []);
   return {...state, reload}
}

export const MessageDetailPanel = ({messageId, onRerun}: MessageDetailPanelProps) => {
   const isAdmin = useRecoilValue(CurrentUserIsAdmin);
   const [activeId, setActiveId] = useState<string>(messageId);
   const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
   const [rerunError, setRerunError] = useState<string | undefined>(undefined);
   const [tab, setTab] = useState<'payload' | 'result'>('payload');

   useEffect(() => {
      setActiveId(messageId);
      setTab('payload');
   }, [messageId]);

   const {data, startPolling, stopPolling} = useMessageDetailPageGetQuery({
      variables: {
         messageId: activeId
      },
      pollInterval: POLL_MS,
      fetchPolicy: "no-cache"
   });
   const [rerunMessage, {loading: rerunning}] = useRerunMessageMutation()

   const msg = data?.IntegrationMessageById as IntegrationMessageFragment | undefined | null;
   const chain = useMessageChain(msg || undefined);
   const terminal = !!msg && isTerminal(msg);

   // Only poll while the connector still owes us an answer.
   useEffect(() => {
      if (terminal) {
         stopPolling();
      } else {
         startPolling(POLL_MS);
      }
   }, [terminal, startPolling, stopPolling]);

   if (!data) {
      return <LinearProgress/>
   }
   if (!msg) {
      return <Box>No details found</Box>
   }

   const rerun = async () => {
      setRerunError(undefined);
      try {
         const {data: result} = await rerunMessage({
            variables: {
               messageId: activeId
            }
         });
         setConfirmOpen(false);
         const created = result?.IntegrationMessageRerun?._id;
         if (created) {
            // Follow the rerun so the operator sees the new attempt instead of the one they left behind.
            setActiveId(created);
         }
         chain.reload();
         if (onRerun) {
            onRerun();
         }
      } catch (error) {
         setConfirmOpen(false);
         setRerunError(error instanceof Error ? error.message : String(error));
      }
   }

   const status = messageStatus(msg);
   const owner = msg.refShop?.name || msg.refLogisticsPartner?.name || msg.refBrandHouse?.name || 'Unknown owner';
   const instance = msg.instance;
   const connector = instance?.connector;
   const errorText = msg.messageFailed ? extractError(msg.result) : undefined;
   const attemptIndex = chain.nodes.findIndex(node => node._id === msg._id);
   const attemptCount = chain.nodes.length;

   return <Box>
      <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"} spacing={2}>
         <Box>
            <Stack direction={"row"} spacing={1} alignItems={"center"} sx={{marginBottom: '6px'}}>
               <Chip size={"small"} color={"primary"} variant={"outlined"} label={msg.message as string}/>
               <Chip size={"small"} color={status.color} label={status.label}/>
               {attemptCount > 1 && attemptIndex >= 0 &&
                  <Chip size={"small"} variant={"outlined"}
                        label={"Attempt " + (attemptIndex + 1) + " of " + attemptCount}/>
               }
               {msg.env && msg.env !== 'production' &&
                  <Chip size={"small"} color={"warning"} variant={"outlined"} label={msg.env}/>
               }
            </Stack>
            <TitleText type={"h2"}>
               {owner} &rarr; {instance?.name || 'Unknown integration'}
            </TitleText>
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
               <BodyText type={"subtitle2"} sx={{color: 'text.secondary'}}>
                  {msg._id} &middot; {dayjs(msg.created).format(TIMESTAMP_FORMAT)}
               </BodyText>
               <Tooltip title={"Copy message id"}>
                  <IconButton size={"small"} onClick={() => copyToClipboard(msg._id)}>
                     <ContentCopyIcon fontSize={"inherit"}/>
                  </IconButton>
               </Tooltip>
            </Stack>
         </Box>
         {isAdmin &&
            <Tooltip title={terminal ? '' : 'The connector has not answered yet'}>
               <span>
                  <Button variant={"contained"} startIcon={<ReplayIcon/>} disabled={!terminal}
                          onClick={() => setConfirmOpen(true)}>
                     Rerun
                  </Button>
               </span>
            </Tooltip>
         }
      </Stack>

      {rerunError &&
         <Alert sx={{marginTop: '10px'}} severity={"error"}
                onClose={() => setRerunError(undefined)}>{rerunError}</Alert>
      }
      {errorText &&
         <Alert sx={{marginTop: '10px'}} severity={"error"}>{errorText}</Alert>
      }

      <Box sx={{
         display: 'grid',
         gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
         gap: '20px',
         marginTop: '16px'
      }}>
         <Box>
            <BodyText type={"subtitle2"} sx={{marginBottom: '6px'}}>Routing</BodyText>
            <Box sx={{display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '16px', rowGap: '4px'}}>
               <Fact label={"Owner"}>{owner} ({msg.refType})</Fact>
               <Fact label={"Connector"}>
                  {connector
                     ? <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
                        <span>{connector.name}</span>
                        <LinkTo to={ConnectorEditPagePath(connector._id)} newTab>
                           <OpenInNewIcon sx={{fontSize: '14px'}}/>
                        </LinkTo>
                     </Stack>
                     : 'Unknown'}
               </Fact>
               <Fact label={"Transport"}>
                  {connector ? connector.type + ' / ' + (connector.dispatcher || 'FUNCTIONS2') : 'Unknown'}
               </Fact>
               <Fact label={"Instance"}>
                  {instance
                     ? instance.name + ' (' + instance.mode + (instance.enabled ? '' : ', disabled') + (instance.deleted ? ', deleted' : '') + ')'
                     : 'Unknown'}
               </Fact>
               <Fact label={"Installed on"}>{instance?.refName || instance?.refType || '-'}</Fact>
               <Fact label={"Sends"}>{msg.sentCount}</Fact>
               {msg.lockedId && <Fact label={"Lock"}>{msg.lockedId}</Fact>}
               {msg.replySequence !== null && msg.replySequence !== undefined &&
                  <Fact label={"Reply sequence"}>{msg.replySequence}</Fact>
               }
            </Box>
         </Box>

         <Box>
            <BodyText type={"subtitle2"} sx={{marginBottom: '6px'}}>Timing</BodyText>
            <MessageTimeline msg={msg}/>
         </Box>
      </Box>

      {attemptCount > 1 &&
         <Box sx={{marginTop: '16px'}}>
            <Divider sx={{marginBottom: '10px'}}/>
            <BodyText type={"subtitle2"} sx={{marginBottom: '6px'}}>
               Rerun history{chain.truncated ? ' (showing the first ' + CHAIN_MAX_NODES + ')' : ''}
            </BodyText>
            <Stack spacing={0.5}>
               {chain.nodes.map((node, index) => {
                  const active = node._id === msg._id;
                  // The lineage is resolved once; the open attempt keeps polling, so read its state from the live message.
                  const nodeStatus = messageStatus(active ? msg : node);
                  return <Stack key={node._id} direction={"row"} spacing={1} alignItems={"center"}
                                onClick={() => setActiveId(node._id)}
                                sx={{
                                   cursor: 'pointer',
                                   padding: '4px 8px',
                                   borderRadius: '4px',
                                   backgroundColor: active ? 'action.selected' : undefined,
                                   '&:hover': {backgroundColor: 'action.hover'}
                                }}>
                     <BodyText type={"body2"} sx={{width: '80px', color: 'text.secondary'}}>
                        {index === 0 ? 'Original' : 'Rerun ' + index}
                     </BodyText>
                     <BodyText type={"body2"} sx={{width: '190px'}}>
                        {dayjs(node.created).format('HH:mm:ss, D. MMM YYYY')}
                     </BodyText>
                     <Chip size={"small"} color={nodeStatus.color} label={nodeStatus.label}/>
                     <BodyText type={"body2"} sx={{color: 'text.secondary'}}>
                        {timeBetween(node.sent, node.resultTs || node.failed) || ''}
                     </BodyText>
                  </Stack>
               })}
            </Stack>
         </Box>
      }

      <Box sx={{marginTop: '16px'}}>
         <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
               <Tab value={"payload"} label={"Request payload"}/>
               <Tab value={"result"} label={"Response"}/>
            </Tabs>
            <Tooltip title={"Copy to clipboard"}>
               <IconButton size={"small"}
                           onClick={() => copyToClipboard(JSON.stringify(tab === 'payload' ? msg.payload : msg.result, null, 2))}>
                  <ContentCopyIcon fontSize={"inherit"}/>
               </IconButton>
            </Tooltip>
         </Stack>
         {tab === 'payload' &&
            <JsonView value={msg.payload} name={"payloadEditor"}/>
         }
         {tab === 'result' && (
            msg.result === null || msg.result === undefined
               ? <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '40px'}}>
                  {terminal
                     ? <BodyText type={"body2"}>The connector stored no response for this message</BodyText>
                     : <>
                        <CircularProgress/>
                        <BodyText type={"body2"}>Waiting for the connector to respond</BodyText>
                     </>
                  }
               </Box>
               : <JsonView value={msg.result} name={"resultEditor"}/>
         )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
         <DialogContent>
            <BodyText type={"body1"}>{RERUN_WARNING}</BodyText>
         </DialogContent>
         <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={rerunning}>Cancel</Button>
            <Button variant={"contained"} onClick={rerun} disabled={rerunning}>Rerun</Button>
         </DialogActions>
      </Dialog>
   </Box>
}

const Fact = ({label, children}: { label: string, children: React.ReactNode }) => {
   return <>
      <BodyText type={"body2"} sx={{color: 'text.secondary'}}>{label}</BodyText>
      <BodyText type={"body2"}>{children}</BodyText>
   </>
}

const JsonView = ({value, name}: { value: any, name: string }) => {
   return <AceEditor
      height={"300px"}
      mode="json"
      readOnly={true}
      theme="tomorrow"
      value={value === null || value === undefined ? '' : JSON.stringify(value, null, 2)}
      name={name}
      editorProps={{$blockScrolling: true}}
      placeholder={"No data"}
      width={"100%"}
   />
}

type TimelineStep = {
   label: string
   at?: Date | null
   color: 'grey' | 'primary' | 'success' | 'error' | 'warning'
   hint?: string
}

const MessageTimeline = ({msg}: { msg: IntegrationMessageFragment }) => {
   const allSteps: TimelineStep[] = [
      {label: 'Created', at: msg.created, color: 'grey'},
      {label: 'Scheduled for', at: msg.sendAt, color: 'warning', hint: 'Delayed delivery'},
      {label: 'Sent', at: msg.sent, color: 'primary', hint: 'Handed to the connector'},
      {label: 'Acknowledged', at: msg.acknowledged, color: 'primary', hint: 'Connector confirmed receipt'},
      {label: 'Responded', at: msg.resultTs, color: msg.messageFailed ? 'error' : 'success'},
      {label: 'Gave up', at: msg.failed, color: 'error', hint: 'Delivery abandoned'}
   ];
   // A scheduled send can sit after the create timestamp, so order by the clock rather than by declaration.
   const steps = allSteps
      .filter(step => !!step.at)
      .sort((a, b) => new Date(a.at as Date).getTime() - new Date(b.at as Date).getTime());

   const first = steps.length > 0 ? steps[0].at : undefined;
   const last = steps.length > 0 ? steps[steps.length - 1].at : undefined;
   const total = timeBetween(first, last);

   return <Box>
      <Timeline sx={{margin: 0, padding: 0, '& .MuiTimelineItem-root:before': {display: 'none'}}}>
         {steps.map((step, index) => {
            const delta = index === 0 ? undefined : timeBetween(steps[index - 1].at, step.at);
            return <TimelineItem key={step.label} sx={{minHeight: '46px'}}>
               <TimelineSeparator>
                  <TimelineDot color={step.color} variant={index === 0 ? 'outlined' : 'filled'}/>
                  {index < steps.length - 1 && <TimelineConnector/>}
               </TimelineSeparator>
               <TimelineContent sx={{paddingTop: 0, paddingBottom: '8px'}}>
                  <Stack direction={"row"} spacing={1} alignItems={"baseline"}>
                     <BodyText type={"body2"} title={step.hint}>{step.label}</BodyText>
                     {delta && <Chip size={"small"} variant={"outlined"} label={'+' + delta}/>}
                  </Stack>
                  <BodyText type={"body2"} sx={{color: 'text.secondary'}}>
                     {dayjs(step.at).format(TIMESTAMP_FORMAT)}
                  </BodyText>
               </TimelineContent>
            </TimelineItem>
         })}
      </Timeline>
      {total && steps.length > 1 &&
         <BodyText type={"body2"} sx={{color: 'text.secondary'}}>Total {total}</BodyText>
      }
   </Box>
}
